import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../lib/api';
import { LanguageSwitcher, SchemeSwitcher, useT } from '../lib/i18n';

/**
 * Signing in, and creating the first account.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS DID NOT EXIST, AND WHY THAT MATTERED SO MUCH
 * ---------------------------------------------------------------------------
 *
 * The API has had `/api/login`, `/api/logout`, `/api/me` and `/api/setup` for a
 * long time. Nothing called any of them. So the dashboard worked in exactly one
 * configuration — authentication switched off, bound to loopback, which the
 * server only permits on a developer's machine.
 *
 * With authentication on, an operator got a red banner reading *"this endpoint
 * needs a signed-in operator or an API token"*, an empty park, and — worse —
 * "No periods for this season yet. Add them on the Periods tab first", which is
 * false: the periods are there and cannot be read. No password field, no link,
 * nowhere to go.
 *
 * ---------------------------------------------------------------------------
 * TWO SCREENS, DECIDED BY THE SERVER
 * ---------------------------------------------------------------------------
 *
 * On a park that has just been deployed there is no operator to sign in as, and
 * a login form is a dead end. `/api/me` says which case this is, because the
 * server is the only thing that knows.
 */

export interface SignInProps {
  /** True when the park has no operator yet: offer to create the first one. */
  setupRequired: boolean;
  /** Called once there is a session, so the app can load itself. */
  onSignedIn: () => void;
}

export function SignIn({ setupRequired, onSignedIn }: SignInProps) {
  const t = useT();
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setProblem(null);

    if (setupRequired) {
      // Checked here as well as at the API. The API's rule is the one that counts;
      // this one exists so the operator is told before they have typed a password
      // twice and pressed a button.
      if (password.length < 12) { setProblem(t('signin.errShort')); return; }
      if (password !== confirm) { setProblem(t('signin.errMismatch')); return; }
    }

    setBusy(true);
    try {
      if (setupRequired) {
        await api.setup(login.trim(), password);
        // Straight in. Making somebody type the password they just chose, into a
        // second form, immediately, is ceremony with no security in it.
        await api.login(login.trim(), password);
      } else {
        await api.login(login.trim(), password);
      }
      onSignedIn();
    } catch (err) {
      // The API's own sentence where it wrote one: "that login and password do
      // not match" is better than anything this screen could invent, and it is
      // deliberately the same for a wrong login and a wrong password.
      setProblem(err instanceof ApiError && err.message ? err.message : t('signin.errFailed'));
      setBusy(false);
    }
  }

  return (
    <main className="signin">
      <header className="signin-head">
        {/* Proper nouns, as in the main header: the product's name and the
            park's are not translated. */}
        <h1>Tariff Studio</h1>
        <div className="signin-prefs">
          {/* Before signing in, deliberately: somebody who cannot read the
              interface cannot be asked to sign in first to change it. */}
          <LanguageSwitcher />
          <SchemeSwitcher />
        </div>
      </header>

      <form className="panel signin-form" onSubmit={submit}>
        <h2>{setupRequired ? t('signin.setupHeading') : t('signin.heading')}</h2>
        <p className="note">
          {setupRequired ? t('signin.setupIntro') : t('signin.intro')}
        </p>

        {problem && <div className="banner bad" role="alert">{problem}</div>}

        <div className="row">
          <label className="wide">
            {t('signin.login')}
            <input
              type="text"
              value={login}
              autoComplete="username"
              autoFocus
              onChange={(e) => setLogin(e.target.value)}
            />
          </label>
        </div>
        <div className="row">
          <label className="wide">
            {t('signin.password')}
            <input
              type="password"
              value={password}
              // `new-password` on first run so a browser offers to generate one,
              // and `current-password` afterwards so it offers the saved one.
              autoComplete={setupRequired ? 'new-password' : 'current-password'}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
        </div>

        {setupRequired && (
          <>
            <div className="row">
              <label className="wide">
                {t('signin.confirm')}
                <input
                  type="password"
                  value={confirm}
                  autoComplete="new-password"
                  onChange={(e) => setConfirm(e.target.value)}
                />
              </label>
            </div>
            <p className="note">{t('signin.passwordNote')}</p>
          </>
        )}

        <div className="row">
          <button type="submit" className="btn primary" disabled={busy}>
            {setupRequired ? t('signin.create') : t('signin.submit')}
          </button>
        </div>
      </form>
    </main>
  );
}
